import { PageHeading } from "@/components/ui/page";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Download, Loader2, Receipt, RefreshCw, Scale, Search, SearchX, TrendingUp, X } from "lucide-react";
import { reportsAPI } from "@/lib/api";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useCurrency } from "@/hooks/useCurrency";
import { usePermission } from "@/hooks/usePermission";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { formatTime } from "@/lib/utils";
import { ReportsFiltersBar } from "../components/ReportsFiltersBar";
import { ReportCard, ReportRow, type RowTag } from "../components/ReportRow";
import { StatTile } from "../components/StatTile";
import { StatGrid } from "../components/ReportBlocks";
import { useReportFilters } from "../hooks/useReportFilters";
import { useReportPrefs } from "../hooks/useReportPrefs";
import { DEFAULT_QUICK_ACCESS, REPORTS, REPORT_GROUPS, canOpenReport, type ReportDefinition } from "../catalog";
import { formatPercent, num, share } from "../lib/columns";
import { downloadWorkbook, type SheetRows } from "../lib/export";
import { rangeText } from "../lib/labels";
import { LOW_STOCK_LIMIT } from "../reports/inventory";
import {
	getDateRangeFromPreset,
	getPreviousRange,
	reportFiltersToParams,
	scopeFilters,
	toYmd,
	type ReportFilterField,
	type ReportFiltersParams,
} from "../types";

/** The home KPIs honor period and branch; each report then offers its own filters. */
const HOME_FIELDS: ReportFilterField[] = ["period", "branch"];

/** "All time" for the dashboard endpoint, which otherwise defaults to the last 12 months. */
const ALL_TIME_START = "2000-01-01";

/** Case- and diacritic-insensitive text for report search (Arabic alef/yaa/taa marbuta forms unified). */
const normalize = (s: string) =>
	s
		.toLowerCase()
		.replace(/[ً-ْـ]/g, "")
		.replace(/[أإآ]/g, "ا")
		.replace(/ى/g, "ي")
		.replace(/ة/g, "ه");

interface Kpis {
	sales: number;
	invoices: number;
	avg: number;
	profit: number;
}

const toKpis = (data: any): Kpis | undefined =>
	data
		? {
				sales: num(data.salesSummary?.totalRevenue),
				invoices: num(data.salesSummary?.totalSales),
				avg: num(data.salesSummary?.averageOrderValue),
				profit: num(data.profitSummary?.profit),
			}
		: undefined;

/**
 * Reports home: headline numbers against the previous period, the reports this person uses most, and every
 * report grouped by the question it answers, searchable. Filters live in the URL and travel into each report.
 */
const ReportsPage: React.FC = () => {
	const { t, i18n } = useTranslation();
	const { user } = useAuth();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const currency = useCurrency();
	const canExport = usePermission("reports.export");
	const { filters, setFilters, search } = useReportFilters();
	const { pinned, opens, togglePin } = useReportPrefs();
	const [query, setQuery] = useState("");
	const [exporting, setExporting] = useState(false);

	const homeFilters = useMemo(() => scopeFilters(filters, HOME_FIELDS), [filters]);
	const params = useMemo(() => reportFiltersToParams(homeFilters), [homeFilters]);
	const previous = getPreviousRange(homeFilters);
	// "Custom" with a date missing waits for both dates instead of loading all time.
	const needsDates = homeFilters.dateRangePreset === "custom" && !params.startDate;
	const kpiParams: ReportFiltersParams = params.startDate
		? params
		: { ...params, startDate: ALL_TIME_START, endDate: toYmd(new Date()) };

	const current = useQuery({
		queryKey: ["report", "home-kpis", kpiParams],
		queryFn: () => reportsAPI.getDashboardSummary(kpiParams),
		enabled: !needsDates,
		staleTime: 60 * 1000,
	});
	const before = useQuery({
		queryKey: ["report", "home-kpis-previous", previous, params.branchId],
		queryFn: () => reportsAPI.getDashboardSummary({ ...previous, branchId: params.branchId }),
		enabled: Boolean(previous),
		staleTime: 60 * 1000,
	});

	const now = toKpis(current.data);
	const prev = previous && before.isSuccess ? toKpis(before.data) : undefined;
	/** undefined = no comparison (all time); null = comparison still loading or unavailable. */
	const prevOf = (pick: (k: Kpis) => number) => (prev ? pick(prev) : previous ? null : undefined);
	const lowStockCount = Array.isArray(current.data?.lowStockProducts) ? current.data.lowStockProducts.length : 0;

	const visible = useMemo(() => REPORTS.filter(r => canOpenReport(user, r)), [user]);
	const title = (r: ReportDefinition) => t(`reports.catalog.${r.key}.title`);

	const tagFor = (r: ReportDefinition): RowTag | undefined => {
		if (r.id !== "low-stock" || lowStockCount === 0) return undefined;
		return {
			tone: "warning",
			label: t("reports.home.itemsTag", {
				count: lowStockCount,
				formatted: lowStockCount >= LOW_STOCK_LIMIT ? `${LOW_STOCK_LIMIT}+` : String(lowStockCount),
			}),
		};
	};

	const q = normalize(query.trim());
	const matches = (r: ReportDefinition) =>
		!q ||
		[
			title(r),
			t(`reports.catalog.${r.key}.description`),
			t(`reports.groups.${r.group}.title`),
			r.id,
			...(r.aliases ?? []),
		]
			.map(normalize)
			.some(text => text.includes(q));
	const filtered = visible.filter(matches);

	// Quick access: pinned first; otherwise the most opened; otherwise sensible defaults.
	const byId = (id: string) => visible.find(r => r.id === id);
	const isReport = (r: ReportDefinition | undefined): r is ReportDefinition => Boolean(r);
	const pinnedReports = pinned.map(byId).filter(isReport);
	const mostUsed = Object.entries(opens)
		.filter(([, count]) => Number(count) > 0)
		.sort((a, b) => Number(b[1]) - Number(a[1]))
		.map(([id]) => byId(id))
		.filter(isReport)
		.slice(0, 3);
	const quickMode = pinnedReports.length ? "pinned" : mostUsed.length ? "mostUsed" : "suggested";
	const quick =
		quickMode === "pinned"
			? pinnedReports
			: quickMode === "mostUsed"
				? mostUsed
				: DEFAULT_QUICK_ACCESS.map(byId).filter(isReport);

	const range = getDateRangeFromPreset(homeFilters);
	const comparison = previous ? (
		<span>
			{t("reports.home.comparedWith")}: <span dir="ltr">{rangeText(previous)}</span>
		</span>
	) : null;

	const handleWorkbook = async () => {
		setExporting(true);
		const failed: string[] = [];
		try {
			const sheets: { name: string; rows: SheetRows }[] = [];
			if (now) {
				sheets.push({
					name: t("reports.export.summarySheet"),
					rows: [
						[
							t("reports.export.summarySheet"),
							range.startDate ? rangeText(range) : t("reports.dateRanges.all"),
						],
						[],
						[t("reports.kpi.sales"), now.sales],
						[t("reports.kpi.invoices"), now.invoices],
						[t("reports.kpi.avgInvoice"), now.avg],
						[t("reports.kpi.netProfit"), now.profit],
					],
				});
			}
			// Each sheet loads on its own; a failed one is left out and named in the result toast.
			const results = await Promise.all(
				visible
					.filter(r => r.sheet)
					.map(r =>
						r.sheet!(reportFiltersToParams(scopeFilters(homeFilters, r.filters)), t, currency).then(
							rows => ({ name: title(r), rows }),
							() => {
								failed.push(title(r));
								return null;
							},
						),
					),
			);
			for (const sheet of results) if (sheet) sheets.push(sheet);
			if (!sheets.length) {
				toast({
					title: t("common.error"),
					description: t("reports.export.nothingToExport"),
					variant: "destructive",
				});
				return;
			}
			const period = range.startDate && range.endDate ? `${range.startDate}_${range.endDate}` : "all";
			downloadWorkbook(sheets, `${t("reports.export.workbookName")} ${period}`, i18n.language);
			const separator = i18n.language?.startsWith("ar") ? "، " : ", ";
			toast(
				failed.length
					? {
							title: t("reports.export.partial"),
							description: t("reports.export.failedSheets", { sheets: failed.join(separator) }),
							variant: "destructive",
						}
					: { title: t("common.success"), description: t("reports.export.success") },
			);
		} catch {
			toast({ title: t("common.error"), description: t("reports.export.error"), variant: "destructive" });
		} finally {
			setExporting(false);
		}
	};

	const searchBox = (
		<div className="relative w-full lg:w-64">
			<Search
				className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
				aria-hidden="true"
			/>
			<Input
				type="search"
				value={query}
				onChange={e => setQuery(e.target.value)}
				placeholder={t("reports.home.searchPlaceholder")}
				aria-label={t("reports.home.searchPlaceholder")}
				className="h-9 pe-9 ps-9"
			/>
			{query && (
				<button
					type="button"
					onClick={() => setQuery("")}
					aria-label={t("common.clear")}
					className="absolute end-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			)}
		</div>
	);

	return (
		<div className="mx-auto max-w-full space-y-6 p-3 sm:p-6">
			<header className="flex flex-col gap-4 border-b pb-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex items-center gap-3">
					<span
						className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-card text-primary shadow-sm"
						aria-hidden="true"
					>
						<BarChart3 className="h-6 w-6" />
					</span>
					<div>
						<PageHeading className="">{t("navigation.reports")}</PageHeading>
						<p className="text-sm text-muted-foreground">
							{t("reports.home.subtitle", { count: visible.length })}
						</p>
					</div>
				</div>
				{canExport && (
					<Button
						variant="outline"
						onClick={handleWorkbook}
						disabled={exporting || needsDates}
						className="self-start sm:self-auto"
					>
						{exporting ? (
							<Loader2 className="h-4 w-4 me-2 animate-spin" aria-hidden="true" />
						) : (
							<Download className="h-4 w-4 me-2" aria-hidden="true" />
						)}
						{exporting ? t("reports.export.preparing") : t("reports.export.workbook")}
					</Button>
				)}
			</header>

			<ReportsFiltersBar
				value={filters}
				onChange={setFilters}
				fields={HOME_FIELDS}
				trailing={searchBox}
				note={comparison}
			/>

			{!q && (
				<section aria-label={t("reports.home.kpis")}>
					{needsDates ? (
						<p className="rounded-2xl border bg-card p-4 text-sm text-muted-foreground">
							{t("reports.dateRange.pickDatesHint")}
						</p>
					) : current.isError ? (
						<div
							role="alert"
							className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card p-4 text-sm"
						>
							<span className="text-muted-foreground">{t("reports.home.kpisFailed")}</span>
							<Button variant="outline" size="sm" onClick={() => current.refetch()}>
								<RefreshCw className="h-4 w-4 me-2" />
								{t("common.retry")}
							</Button>
						</div>
					) : (
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.sales")}
								value={now?.sales}
								format="money"
								currency={currency}
								previous={prevOf(k => k.sales)}
								loading={current.isPending}
								to={`/reports/sales-trend${search}`}
							/>
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.invoices")}
								value={now?.invoices}
								previous={prevOf(k => k.invoices)}
								loading={current.isPending}
								to={`/reports/sales-invoices${search}`}
							/>
							<StatTile
								icon={BarChart3}
								label={t("reports.kpi.avgInvoice")}
								value={now?.avg}
								format="money"
								currency={currency}
								previous={prevOf(k => k.avg)}
								loading={current.isPending}
								to={`/reports/product-sales${search}`}
							/>
							<StatTile
								icon={Scale}
								label={t("reports.kpi.netProfit")}
								value={now?.profit}
								format="money"
								currency={currency}
								previous={prevOf(k => k.profit)}
								loading={current.isPending}
								tone={now && now.profit < 0 ? "negative" : "default"}
								hint={
									now && now.sales
										? t("reports.kpi.marginHint", {
												value: formatPercent(share(now.profit, now.sales)),
											})
										: undefined
								}
								to={`/reports/profit-loss${search}`}
							/>
						</StatGrid>
					)}
				</section>
			)}

			{!q && quick.length > 0 && (
				<section aria-labelledby="reports-quick">
					<SectionHead
						id="reports-quick"
						mark="★"
						title={t(`reports.home.quick.${quickMode}`)}
						count={quick.length}
						hint={quickMode === "suggested" ? t("reports.home.quickHint") : undefined}
					/>
					<ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{quick.map(r => (
							<ReportCard
								key={r.id}
								report={r}
								search={search}
								pinned={pinned.includes(r.id)}
								onTogglePin={() => togglePin(r.id)}
								tag={tagFor(r)}
							/>
						))}
					</ul>
				</section>
			)}

			{filtered.length === 0 ? (
				<div className="rounded-2xl border bg-card">
					<EmptyState
						icon={SearchX}
						title={t("reports.home.noMatch", { query: query.trim() })}
						description={t("reports.home.noMatchHint")}
					/>
				</div>
			) : (
				<div className="grid items-start gap-6 xl:grid-cols-2">
					{REPORT_GROUPS.map((group, index) => {
						const rows = filtered.filter(r => r.group === group);
						if (!rows.length) return null;
						return (
							<section
								key={group}
								aria-labelledby={`reports-group-${group}`}
								className="min-w-0 rounded-2xl border bg-card p-4 shadow-sm sm:p-5"
							>
								<SectionHead
									id={`reports-group-${group}`}
									mark={String(index + 1).padStart(2, "0")}
									title={t(`reports.groups.${group}.title`)}
									count={rows.length}
									hint={t(`reports.groups.${group}.description`)}
								/>
								<ul>
									{rows.map(r => (
										<ReportRow
											key={r.id}
											report={r}
											search={search}
											pinned={pinned.includes(r.id)}
											onTogglePin={() => togglePin(r.id)}
											tag={tagFor(r)}
										/>
									))}
								</ul>
							</section>
						);
					})}
				</div>
			)}

			<footer className="flex flex-col items-center gap-2 pt-2 text-center text-xs text-muted-foreground">
				<span className="h-px w-10 bg-primary/40" aria-hidden="true" />
				<span>
					{current.dataUpdatedAt
						? `${t("reports.home.updatedAt", { time: formatTime(current.dataUpdatedAt) })} · `
						: null}
					<button
						type="button"
						onClick={() => queryClient.invalidateQueries({ queryKey: ["report"] })}
						className="inline-flex items-center gap-1 rounded text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						<RefreshCw className="h-3 w-3" aria-hidden="true" />
						{t("common.refresh")}
					</button>
				</span>
			</footer>
		</div>
	);
};

/** Ledger-style section heading: number, title, count, then a hairline to the edge. */
function SectionHead({
	id,
	mark,
	title,
	count,
	hint,
}: {
	id: string;
	mark: string;
	title: string;
	count: number;
	hint?: string;
}) {
	return (
		<div className="mb-3">
			<div className="flex items-baseline gap-3">
				<span className="text-sm font-semibold text-primary" aria-hidden="true">
					{mark}
				</span>
				<h2 id={id} className="text-lg font-bold">
					{title}
				</h2>
				<span className="text-xs text-muted-foreground">{count}</span>
				<span className="h-px flex-1 self-center bg-border" aria-hidden="true" />
			</div>
			{hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
		</div>
	);
}

export default ReportsPage;
